/**
 * /vs/:competitor — full-fat comparison landing.
 *
 * Renders the structured payload from `GET /api/comparison/{competitor}`
 * with five sections optimised for bottom-of-funnel SEO conversions:
 *   1. Hero (one-line tagline + dual CTA)
 *   2. Side-by-side comparison table (we vs them)
 *   3. Migration guide (3-step "how to switch")
 *   4. FAQ — long-tail keyword magnet
 *   5. Cross-links to other /vs/ pages (internal SEO graph)
 *
 * Replaces the older Semrush/Ahrefs-only file; the API is the single
 * source of competitor truth so adding a fifth one is a one-line backend
 * edit.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, CheckCircle, X, ChevronRight, Crown,
  Globe, BarChart3, Loader2, Quote,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { LiveCounter } from "../components/LiveCounter";
import { TrustBadgeRow } from "../components/TrustBadgeRow";
import { captureRefFromQuery } from "./AffiliateRedirect";
import { API } from "../config";
import SwitcherCouponBanner from "../components/SwitcherCouponBanner";
import SwitcherExitIntentModal from "../components/SwitcherExitIntentModal";

const COPY = {
  sk: {
    eyebrow: "POROVNANIE",
    h1_prefix: "TopRankPulse vs",
    cta_primary: "Skús TopRankPulse zadarmo",
    cta_secondary: "Pozri rozdiely nižšie",
    feature_header_them: "Konkurent",
    feature_header_us: "TopRankPulse",
    migration_title: "Ako prejsť za 5 minút",
    faq_title: "Časté otázky",
    sponsors_title: "Tiež zvažuješ",
    other_title: "Porovnaj aj s",
    final_cta_h: "Pripravený prepnúť?",
    final_cta_sub: "30-sekundový audit. Bez kreditky. 26 jazykov.",
    final_cta_btn: "Spustiť audit zadarmo",
    save_yearly: "Ušetríš €960+/rok",
    not_found: "Konkurent neexistuje",
  },
  en: {
    eyebrow: "COMPARISON",
    h1_prefix: "TopRankPulse vs",
    cta_primary: "Try TopRankPulse free",
    cta_secondary: "See differences below",
    feature_header_them: "Competitor",
    feature_header_us: "TopRankPulse",
    migration_title: "How to switch in 5 minutes",
    faq_title: "FAQs",
    sponsors_title: "Also considering",
    other_title: "Also compare with",
    final_cta_h: "Ready to switch?",
    final_cta_sub: "30-second audit. No credit card. 26 languages.",
    final_cta_btn: "Run free audit",
    save_yearly: "Save €960+/yr",
    not_found: "Competitor not found",
  },
};

export default function ComparisonPage() {
  const { competitor } = useParams();
  const { language } = useLanguage();
  const t = COPY[language] || COPY.en;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setNotFound(false);
    // Capture ?ref= from URL into localStorage so the partner gets credit
    // even if the visitor doesn't click any CTA on this page directly.
    captureRefFromQuery();
    axios.get(`${API}/comparison/${competitor}`, { params: { lang: language || "sk" } })
      .then(r => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setNotFound(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [competitor, language]);

  // Inject SEO meta tags + JSON-LD once we have the data. Keeping it in
  // a useEffect so we don't drag react-helmet into the bundle.
  useEffect(() => {
    if (!data) return;
    const title = `${t.h1_prefix} ${data.name}: ktoré SEO + AI viditeľnosť nástroj vyhráva? · TopRankPulse`;
    document.title = title;
    setMeta("description", `${data.tagline} Plné porovnanie ${data.name} vs TopRankPulse: cena, AI engines, 26 jazykov, lifetime deal.`);
    setMeta("og:title", title, true);
    setMeta("og:description", data.tagline, true);
    setMeta("og:image", `${API}/comparison/og/${data.slug}.png`, true);
    setMeta("twitter:card", "summary_large_image");
    upsertJsonLd(data, language);
    return () => removeJsonLd();
  }, [data, language, t.h1_prefix]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-zinc-400 mb-4">{t.not_found}</p>
          <Link to="/" className="text-[#FFE600] underline">← TopRankPulse</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="comparison-page">
      <SwitcherCouponBanner slug={competitor} />
      <SwitcherExitIntentModal slug={competitor} />
      <header className="border-b border-zinc-800 py-4 px-6 sticky top-0 bg-[#0a0a0a]/95 backdrop-blur-sm z-40">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <Link to="/" className="font-black text-lg font-['Cabinet_Grotesk']">TopRankPulse</Link>
          <Link to="/" className="text-xs px-4 py-2 bg-[#FFE600] text-black font-bold uppercase tracking-wider">
            {t.cta_primary}
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-3" data-testid="comparison-eyebrow">
          {t.eyebrow}
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] mb-5 leading-[0.95]"
            data-testid="comparison-h1">
          {t.h1_prefix} <span style={{ color: data.color }}>{data.name}</span>
        </h1>
        <p className="text-base sm:text-lg text-zinc-400 mb-8 max-w-3xl" data-testid="comparison-tagline">
          {data.tagline}
        </p>

        <div className="flex flex-wrap gap-3 mb-6">
          <Link to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#FFE600] text-black font-black uppercase tracking-wider hover:bg-yellow-300 transition-colors"
            data-testid="comparison-cta-primary">
            {t.cta_primary} <ArrowRight className="w-5 h-5" />
          </Link>
          <a href="#features"
            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-zinc-700 text-zinc-200 font-bold uppercase tracking-wider hover:border-white"
            data-testid="comparison-cta-secondary">
            {t.cta_secondary}
          </a>
        </div>

        <div><LiveCounter language={language} /></div>
      </section>

      {/* FEATURES TABLE */}
      <section id="features" className="max-w-5xl mx-auto px-4 pb-16">
        <div className="bg-[#141414] border-2 border-zinc-800 overflow-hidden" data-testid="comparison-features-table">
          <div className="grid grid-cols-12 bg-[#0a0a0a] border-b-2 border-zinc-800 text-[10px] font-mono uppercase tracking-wider">
            <div className="col-span-5 px-4 py-3 text-zinc-500">Feature</div>
            <div className="col-span-3 px-4 py-3 text-center" style={{ color: data.color }}>{data.name}</div>
            <div className="col-span-4 px-4 py-3 text-center text-[#FFE600] flex items-center justify-center gap-1">
              <Crown className="w-3 h-3" />{t.feature_header_us}
            </div>
          </div>
          {data.trp_wins.map((row, i) => (
            <div key={i} className="grid grid-cols-12 border-b border-zinc-800/60 hover:bg-zinc-900/40 transition-colors"
                 data-testid={`comparison-feature-${i}`}>
              <div className="col-span-5 px-4 py-3 text-sm text-zinc-200">{row[0]}</div>
              <div className="col-span-3 px-4 py-3 text-center text-sm text-zinc-400">
                <FeatureCell val={row[1]} dim />
              </div>
              <div className="col-span-4 px-4 py-3 text-center text-sm font-bold text-[#10B981]">
                <FeatureCell val={row[2]} />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-zinc-500" data-testid="comparison-save-line">
          <Quote className="w-3 h-3" />
          <span>
            {data.slug === "ahrefs"
              ? "Pri Ahrefs Lite ($129/mes) ušetríš ~€1,080/rok prechodom na TopRankPulse."
              : `${t.save_yearly} pri ročnom porovnaní s ${data.name}.`}
          </span>
        </div>
      </section>

      {/* MIGRATION */}
      {(data.migration_steps_sk?.length || 0) > 0 && (
        <section className="max-w-4xl mx-auto px-4 pb-16">
          <h2 className="text-2xl sm:text-3xl font-black font-['Cabinet_Grotesk'] mb-6" data-testid="comparison-migration-title">
            {t.migration_title}
          </h2>
          <ol className="space-y-3" data-testid="comparison-migration-steps">
            {data.migration_steps_sk.map((step, i) => (
              <li key={i} className="flex items-start gap-4 bg-[#141414] border border-zinc-800 p-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FFE600] text-black font-black flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="text-zinc-200 text-sm sm:text-base">{step}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* FAQ */}
      {(data.faq_sk?.length || 0) > 0 && (
        <section className="max-w-4xl mx-auto px-4 pb-16">
          <h2 className="text-2xl sm:text-3xl font-black font-['Cabinet_Grotesk'] mb-6" data-testid="comparison-faq-title">
            {t.faq_title}
          </h2>
          <div className="space-y-2" data-testid="comparison-faq">
            {data.faq_sk.map(([q, a], i) => (
              <details key={i} className="bg-[#141414] border border-zinc-800 group" data-testid={`comparison-faq-${i}`}>
                <summary className="cursor-pointer px-4 py-3 font-bold text-zinc-100 list-none flex items-center justify-between hover:bg-zinc-900/40">
                  <span>{q}</span>
                  <ChevronRight className="w-4 h-4 text-zinc-500 transition-transform group-open:rotate-90" />
                </summary>
                <p className="px-4 pb-4 text-sm text-zinc-400 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* SPONSORS — only shown when an admin has wired one up */}
      {(data.sponsors?.length || 0) > 0 && (
        <section className="max-w-4xl mx-auto px-4 pb-12">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3" data-testid="comparison-sponsors-title">
            {t.sponsors_title}
          </p>
          <div className="flex flex-wrap gap-3" data-testid="comparison-sponsors">
            {data.sponsors.map((s, i) => (
              <a key={i} href={s.sponsor_url} target="_blank" rel="noreferrer"
                 className="px-4 py-3 border border-zinc-800 hover:border-zinc-600 bg-[#0f0f0f] inline-flex items-center gap-3"
                 data-testid={`comparison-sponsor-${i}`}>
                {s.logo_url && <img src={s.logo_url} alt={s.sponsor_name} className="h-6 w-auto" loading="lazy" />}
                <div>
                  <p className="font-bold text-sm text-white">{s.sponsor_name}</p>
                  {s.tagline && <p className="text-[11px] text-zinc-500">{s.tagline}</p>}
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* CROSS-LINKS */}
      {(data.other_competitors?.length || 0) > 0 && (
        <section className="max-w-4xl mx-auto px-4 pb-16">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">
            {t.other_title}
          </p>
          <div className="flex flex-wrap gap-2" data-testid="comparison-cross-links">
            {data.other_competitors.map((c) => (
              <Link key={c.slug} to={`/vs/${c.slug}`}
                className="px-3 py-2 border border-zinc-800 hover:border-zinc-500 text-sm text-zinc-300 hover:text-white inline-flex items-center gap-2"
                data-testid={`comparison-cross-${c.slug}`}>
                <BarChart3 className="w-3 h-3" />
                TopRankPulse vs {c.name}
                <span className="text-[10px] text-zinc-600">· {c.price_label}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* TRUST BADGES */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-4">
          <TrustBadgeRow language={language} />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-zinc-800 py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] mb-3">{t.final_cta_h}</h2>
          <p className="text-zinc-400 mb-6">{t.final_cta_sub}</p>
          <Link to="/"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#FFE600] text-black font-black uppercase tracking-wider text-base hover:bg-yellow-300 transition-colors"
            data-testid="comparison-final-cta">
            {t.final_cta_btn} <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-xs text-zinc-500 mt-3 font-mono flex items-center justify-center gap-1">
            <Globe className="w-3 h-3" /> 26 languages · Lifetime deal €297 also available
          </p>
        </div>
      </section>
    </div>
  );
}

function FeatureCell({ val, dim }) {
  if (val === true)  return <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto" />;
  if (val === false) return <X className={`w-5 h-5 mx-auto ${dim ? "text-red-500/40" : "text-red-500/80"}`} />;
  if (typeof val === "string") {
    return <span className={dim ? "text-zinc-400" : "text-emerald-400 font-bold"}>{val}</span>;
  }
  return null;
}

function setMeta(name, value, og = false) {
  if (typeof document === "undefined") return;
  const attr = og ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertJsonLd(data, lang) {
  removeJsonLd();
  const ld = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "TopRankPulse",
    "description": data.tagline,
    "brand": { "@type": "Brand", "name": "TopRankPulse" },
    "offers": { "@type": "Offer", "price": "39", "priceCurrency": "EUR" },
    "review": {
      "@type": "Review",
      "reviewRating": { "@type": "Rating", "ratingValue": "4.9", "bestRating": "5" },
      "author": { "@type": "Organization", "name": "TopRankPulse" },
      "reviewBody": `TopRankPulse vs ${data.name}: ${data.tagline}`,
    },
  };
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = "comparison-jsonld";
  s.textContent = JSON.stringify(ld);
  document.head.appendChild(s);
}

function removeJsonLd() {
  if (typeof document === "undefined") return;
  const existing = document.getElementById("comparison-jsonld");
  if (existing) existing.remove();
}
